import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'fias-sidebar-collapsed';
const MOBILE_BREAKPOINT = 1100; // increased from 1024 for conversation history button

/**
 * The suggested-arches sidebar's collapse state.
 *
 * Two layers, deliberately:
 *  - the user's PREFERENCE (`toggle` / `setCollapsed`): persisted to
 *    localStorage on desktop and shared by every arche, as it always was;
 *  - an OVERRIDE (`overrideCollapsed` / `clearOverride`): what a plugin arche
 *    sets through the SDK (`useFiasChrome().setSidebarCollapsed`). It wins
 *    while present and is never written to storage, so a plugin collapsing
 *    the sidebar on its own page cannot change the starting state of every
 *    other arche, now or after a reload. The plugin renderer clears it when
 *    the plugin leaves; a user's own click clears it too (the user outranks
 *    the plugin, and their click IS a preference).
 */
export function useSidebarState() {
  const [preference, setPreference] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // On mobile, always start collapsed
    if (window.innerWidth < MOBILE_BREAKPOINT) return true;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });
  const [override, setOverride] = useState<boolean | null>(null);

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  // Listen for viewport changes
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
      // Auto-collapse when entering mobile viewport
      if (e.matches) {
        setPreference(true);
      }
    };

    // Set initial state
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Only persist the PREFERENCE, and only on desktop. An override never
  // reaches storage — see the note above.
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEY, String(preference));
    }
  }, [preference, isMobile]);

  const isCollapsed = override ?? preference;
  // The effective value at call time, for `toggle` (a plain updater would
  // flip the preference, not what the user is looking at).
  const isCollapsedRef = useRef(isCollapsed);
  isCollapsedRef.current = isCollapsed;

  const toggle = useCallback(() => {
    setOverride(null);
    setPreference(!isCollapsedRef.current);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setOverride(null);
    setPreference(collapsed);
  }, []);

  const overrideCollapsed = useCallback((collapsed: boolean) => {
    setOverride(collapsed);
  }, []);

  const clearOverride = useCallback(() => {
    setOverride(null);
  }, []);

  return {
    isCollapsed,
    isMobile,
    toggle,
    setCollapsed,
    overrideCollapsed,
    clearOverride,
  };
}
